import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BellRing, Check, Copy, Loader2, Pencil, Send, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { API } from "@/lib/api";
import { copyText } from "@/components/SocialShareRow";
import { StatusBadge } from "@/pages/MarketListPage";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

function Gallery({ media }) {
  const [active, setActive] = useState(0);
  if (!media.length) return null;
  const m = media[active];
  const src = `${API}/market/media/${m.id}`;
  return (
    <div className="space-y-2" data-testid="listing-gallery">
      <div className="rounded-xl overflow-hidden bg-black/90 flex items-center justify-center max-h-[70vh]">
        {m.kind === "video" ? (
          <video key={m.id} src={src} controls playsInline className="max-h-[70vh] w-full" data-testid="listing-video" />
        ) : (
          <img key={m.id} src={src} alt="" className="max-h-[70vh] w-auto object-contain" data-testid="listing-image" />
        )}
      </div>
      {media.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {media.map((x, i) => (
            <button key={x.id} type="button" onClick={() => setActive(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === active ? "border-olive" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
              {x.kind === "video" ? <video src={`${API}/market/media/${x.id}`} className="w-full h-full object-cover" muted /> : <img src={`${API}/market/media/${x.id}`} alt="" className="w-full h-full object-cover" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminReplies({ l, t, isAdmin, onReplied }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/market/listings/${l.id}/replies`, { body });
      setBody("");
      onReplied();
    } catch (e) { toast.error(e?.response?.data?.detail || t("share.failed")); }
    setBusy(false);
  };
  if (!isAdmin && !l.replies?.length) return null;
  return (
    <div className="space-y-2" data-testid="listing-replies">
      {l.replies?.map((r) => (
        <div key={r.id} className="bg-olive/10 border border-olive/30 rounded-xl p-3 text-sm" data-testid="listing-reply">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-olive"><ShieldCheck className="w-3.5 h-3.5" />{r.admin_name} · {new Date(r.created_at).toLocaleString()}</div>
          <p className="mt-1 whitespace-pre-wrap text-espresso">{r.body}</p>
        </div>
      ))}
      {isAdmin && (
        <div className="flex gap-2 items-end">
          <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("market.reply_placeholder")} data-testid="reply-input" />
          <Button size="sm" onClick={send} disabled={busy || !body.trim()} data-testid="reply-send"><Send className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}

export default function MarketListingPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api.get(`/market/listings/${id}`).then(({ data }) => setL(data)).catch(() => setL(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (l === null) return <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />;
  if (l === false) return <div className="text-sm text-espresso/60" data-testid="listing-missing">{t("market.not_found")}</div>;

  const doneStatus = l.type === "sell" ? "sold" : "bought";
  const toggleStatus = async () => {
    const { data } = await api.patch(`/market/listings/${l.id}`, { status: l.status === "open" ? doneStatus : "open" });
    setL(data);
    toast.success(data.status === "open" ? t("market.reopened") : t(`market.${doneStatus}`));
  };
  const remove = async () => {
    await api.delete(`/market/listings/${l.id}`);
    toast.success(t("market.deleted"));
    navigate(`/market/${l.type}`);
  };
  const remindOwner = async () => {
    try { await api.post(`/market/admin/listings/${l.id}/remind`); toast.success(t("stale.reminded")); load(); } catch { toast.error(t("stale.failed")); }
  };
  const copyDesc = async () => {
    if (await copyText(l.description)) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="listing-page">
      <div className="flex items-center gap-3">
        <Link to={`/market/${l.type}`} className="text-espresso/60 hover:text-espresso" data-testid="listing-back"><ArrowLeft className="w-5 h-5 rtl:rotate-180" /></Link>
        <span className="text-xs font-bold uppercase tracking-widest text-olive">{t(l.type === "sell" ? "market.selling" : "market.buying")} · {t(`market.species_${l.species}`)}</span>
      </div>
      <div className="flex flex-wrap items-start gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-espresso flex-1" data-testid="listing-title">{l.title}</h1>
        <StatusBadge status={l.status} t={t} size="lg" />
      </div>
      <div className="text-xs text-espresso/60">{l.owner_name} · {new Date(l.created_at).toLocaleString()}</div>

      <Gallery media={l.media} />

      <div className="bg-white rounded-xl border border-app-border p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-widest text-olive font-bold">{t("market.description")}</span>
          <Button variant="ghost" size="sm" onClick={copyDesc} className="h-7 text-xs gap-1" data-testid="copy-description">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{t("market.copy")}
          </Button>
        </div>
        <p className="whitespace-pre-wrap text-sm text-espresso select-all" dir="auto" data-testid="listing-description">{l.description}</p>
      </div>

      {l.can_edit && (
        <div className="flex flex-wrap gap-2" data-testid="owner-actions">
          <Button onClick={toggleStatus} variant={l.status === "open" ? "default" : "outline"} className={l.status === "open" ? "bg-red-600 hover:bg-red-700 text-white" : ""} data-testid="mark-status-button">
            {l.status === "open" ? t(`market.mark_${doneStatus}`) : t("market.mark_open")}
          </Button>
          <Button variant="outline" asChild data-testid="edit-listing-button"><Link to={`/market/listing/${l.id}/edit`}><Pencil className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />{t("common.edit")}</Link></Button>
          <Button variant="outline" onClick={() => setConfirm(true)} className="text-red-700" data-testid="delete-listing-button"><Trash2 className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />{t("common.delete")}</Button>
          {user?.role === "admin" && !l.is_owner && l.status === "open" && (
            <Button variant="outline" onClick={remindOwner} className="text-amber-800 border-amber-300" data-testid="remind-owner-button"><BellRing className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />{t("stale.remind")}</Button>
          )}
        </div>
      )}

      <AdminReplies l={l} t={t} isAdmin={user?.role === "admin"} onReplied={load} />

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("market.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("market.delete_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-yes">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
